import { FC } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';
import {
  Heading,
  H2,
  H3,
  Container,
  Text,
  Anchor,
  Link
} from '../../components';
import {
  RealtimeSystemInformationExample,
  RealtimeStationInformationExample,
  RealtimeGbfsJsonExample
} from './Examples';
import { openDataQuery } from '../../core-types';
import { messages as openDataMessages } from './';
import { CodeBlock } from './CodeBlock';
import { InlineCode } from './InlineCode';
import { BlockContent } from '../../components/BlockContent';
import { useSettingsQuery } from '../../lib/core/SettingsQuery';

type Props = {
  data: openDataQuery;
  showTripHistory: boolean | null;
};

const OpenDataWrapper = styled.div`
  max-width: 45em;
  margin-left: auto;
  margin-right: auto;
`;

const messages = defineMessages({
  documentation: {
    id: 'documentation',
    defaultMessage: 'Documentation'
  },
  systemTitle: {
    id: 'modules.OpenData.Api.systemTitle',
    defaultMessage: 'System'
  },
  autoDiscoveryInformation: {
    id: 'modules.OpenData.Api.autoDiscoveryInformation',
    defaultMessage:
      'Auto-discovery file that links to all of the other files published by the system.'
  },
  autoDiscoveryEndpoint: {
    id: 'modules.OpenData.Api.autoDiscoveryEndpoint',
    defaultMessage:
      'Endpoint: {autoDiscoveryEndPointLink} ({autoDiscoveryEndPointDocumentation})'
  },
  systemInformation: {
    id: 'modules.OpenData.Api.systemInformation',
    defaultMessage: 'Machine readable basic information about {systemName}'
  },
  systemEndpoint: {
    id: 'modules.OpenData.Api.systemEndpoint',
    defaultMessage:
      'Endpoint: {systemEndPointLink} ({systemEndPointDocumentation})'
  },
  stationsTitle: {
    id: 'modules.OpenData.Api.stationsTitle',
    defaultMessage: 'Stations'
  },
  stationsInformation: {
    id: 'modules.OpenData.Api.stationsInformation',
    defaultMessage:
      'Information about all stations, including geolocation, addresses and descriptions'
  },
  stationsEndpoint: {
    id: 'modules.OpenData.Api.stationsEndpoint',
    defaultMessage:
      'Endpoint: {stationsEndPointLink} ({stationsEndPointDocumentation})'
  },
  availabilityTitle: {
    id: 'modules.OpenData.Api.availabilityTitle',
    defaultMessage: 'Availability'
  },
  availabilityInformation: {
    id: 'modules.OpenData.Api.availabilityInformation',
    defaultMessage: 'Bike and dock availability'
  },
  availabilityEndpoint: {
    id: 'modules.OpenData.Api.availabilityEndpoint',
    defaultMessage:
      'Endpoint: {availabilityEndPointLink} ({availabilityEndPointDocumentation})'
  },
  curl: {
    id: 'modules.OpenData.Api.curl',
    defaultMessage: 'CURL request'
  },
  response: {
    id: 'modules.OpenData.Api.response',
    defaultMessage: 'Response'
  },
  historicalDataTitle: {
    id: 'modules.OpenData.Api.historicalDataTitle',
    defaultMessage: 'Historical data'
  },
  historicalDataText: {
    id: 'modules.OpenData.Api.historicalDataText',
    defaultMessage: 'You may also {historicalDataLink}'
  },
  historicalDataLinkText: {
    id: 'modules.OpenData.Api.historicalDataLinkText',
    defaultMessage: 'download anonymised trip records'
  },
  endpoints: {
    id: 'modules.OpenData.Api.endpoints',
    defaultMessage: 'Endpoints'
  },
  identificationTitle: {
    id: 'modules.OpenData.Api.identificationTitle',
    defaultMessage: 'Identification in header'
  },
  identificationDescription: {
    id: 'modules.OpenData.Api.identificationDescription',
    defaultMessage: `All requests to our real time API should send the {clientIdentifierHeader} header. This should contain a value that describes the application accessing the API. The value should contain your company/organization name, follwed by a dash and the application's name, like <code>mycompany-travelplanner</code> or <code>myname-citymonitor</code>.`
  }
});

const H4: FC<{}> = ({ children }) => {
  return (
    <Text as="h4" mt="xs" typoStyle="xxs">
      {children}
    </Text>
  );
};

const OpenData = ({ data, showTripHistory }: Props) => {
  const { data: settingsData } = useSettingsQuery();
  if (
    !data.openData ||
    !settingsData ||
    !settingsData.settings ||
    !settingsData.system
  ) {
    return null;
  }
  const { openData } = data;

  const systemDomain = settingsData.system.publicDomain;
  const systemId = settingsData.system.id;
  const systemName = settingsData.settings.title;

  if (!systemDomain) {
    return null;
  }

  return (
    <Container mt={6} mb={8}>
      <OpenDataWrapper>
        <Heading textAlign="center" mb={5}>
          {openData.realtimeTitle}
        </Heading>
        {openData.realtimeDescription && (
          <Text lead textAlign="center">
            <BlockContent blocks={openData.realtimeDescription} />
          </Text>
        )}

        {openData.licence && openData.licence.licence_title && (
          <Text as="p">
            {openData.licence.licence_url ? (
              <FormattedMessage
                {...openDataMessages.licence}
                values={{
                  licence: (
                    <Anchor href={openData.licence.licence_url}>
                      {openData.licence.licence_title}
                    </Anchor>
                  )
                }}
              />
            ) : (
              <FormattedMessage
                {...openDataMessages.licence}
                values={{ licence: openData.licence.licence_title }}
              />
            )}
          </Text>
        )}

        <H2 mt="sm">
          <FormattedMessage {...messages.identificationTitle} />
        </H2>
        <Text as="p">
          <FormattedMessage
            {...messages.identificationDescription}
            values={{
              // eslint-disable-next-line
              code: msg => <InlineCode>{msg}</InlineCode>,
              clientIdentifierHeader: <InlineCode>Client-Identifier</InlineCode>
            }}
          />
        </Text>

        <H2 mt="sm">
          <FormattedMessage {...messages.endpoints} />
        </H2>

        <H3 mt="xs">Auto discovery</H3>
        <Text as="p">
          <FormattedMessage
            {...messages.autoDiscoveryInformation}
            values={{ systemName }}
          />
        </Text>
        <Text as="p">
          <FormattedMessage
            {...messages.autoDiscoveryEndpoint}
            values={{
              autoDiscoveryEndPointLink: (
                <Anchor
                  href={`https://gbfs.urbansharing.com/${systemDomain}/gbfs.json`}
                >
                  gbfs.json
                </Anchor>
              ),
              autoDiscoveryEndPointDocumentation: (
                <Anchor href="https://github.com/NABSA/gbfs/blob/df473ca4adbff982d67b50ac00b625191591d8f8/gbfs.md#gbfsjson">
                  <FormattedMessage {...messages.documentation} />
                </Anchor>
              )
            }}
          />
        </Text>
        <H4>
          <FormattedMessage {...messages.curl} />
        </H4>
        <CodeBlock>
          {`curl -H "Client-Identifier: IDENTIFIER" https://gbfs.urbansharing.com/${systemDomain}/gbfs.json`}
        </CodeBlock>
        {RealtimeGbfsJsonExample(systemId) && (
          <>
            <H4>
              <FormattedMessage {...messages.response} />
            </H4>
            <CodeBlock>{RealtimeGbfsJsonExample(systemId)}</CodeBlock>
          </>
        )}

        <H3 mt="md">
          <FormattedMessage {...messages.systemTitle} />
        </H3>
        <Text as="p">
          <FormattedMessage
            {...messages.systemInformation}
            values={{ systemName }}
          />
        </Text>
        <Text as="p">
          <FormattedMessage
            {...messages.systemEndpoint}
            values={{
              systemEndPointLink: (
                <Anchor
                  href={`https://gbfs.urbansharing.com/${systemDomain}/system_information.json`}
                >
                  system_information.json
                </Anchor>
              ),
              systemEndPointDocumentation: (
                <Anchor href="https://github.com/NABSA/gbfs/blob/df473ca4adbff982d67b50ac00b625191591d8f8/gbfs.md#system_informationjson">
                  <FormattedMessage {...messages.documentation} />
                </Anchor>
              )
            }}
          />
        </Text>
        <H4>
          <FormattedMessage {...messages.curl} />
        </H4>
        <CodeBlock>
          {`curl -H "Client-Identifier: IDENTIFIER" \\
  https://gbfs.urbansharing.com/${systemDomain}/system_information.json`}
        </CodeBlock>
        {RealtimeSystemInformationExample(systemId) && (
          <>
            <H4>
              <FormattedMessage {...messages.response} />
            </H4>
            <CodeBlock>{RealtimeSystemInformationExample(systemId)}</CodeBlock>
          </>
        )}

        <H3 mt="md">
          <FormattedMessage {...messages.stationsTitle} />
        </H3>
        <Text as="p">
          <FormattedMessage
            {...messages.stationsInformation}
            values={{ systemName }}
          />
        </Text>
        <Text as="p">
          <FormattedMessage
            {...messages.stationsEndpoint}
            values={{
              stationsEndPointLink: (
                <Anchor
                  href={`https://gbfs.urbansharing.com/${systemDomain}/station_information.json`}
                >
                  station_information.json
                </Anchor>
              ),
              stationsEndPointDocumentation: (
                <Anchor href="https://github.com/NABSA/gbfs/blob/df473ca4adbff982d67b50ac00b625191591d8f8/gbfs.md#station_informationjson">
                  <FormattedMessage {...messages.documentation} />
                </Anchor>
              )
            }}
          />
        </Text>
        <H4>
          <FormattedMessage {...messages.curl} />
        </H4>
        <CodeBlock>
          {`curl -H "Client-Identifier: IDENTIFIER" \\
  https://gbfs.urbansharing.com/${systemDomain}/station_information.json`}
        </CodeBlock>
        {RealtimeStationInformationExample(systemId) && (
          <>
            <H4>
              <FormattedMessage {...messages.response} />
            </H4>
            <CodeBlock>{RealtimeStationInformationExample(systemId)}</CodeBlock>
          </>
        )}

        <H3 mt="md">
          <FormattedMessage {...messages.availabilityTitle} />
        </H3>
        <Text as="p">
          <FormattedMessage
            {...messages.availabilityInformation}
            values={{ systemName }}
          />
        </Text>
        <Text as="p">
          <FormattedMessage
            {...messages.availabilityEndpoint}
            values={{
              availabilityEndPointLink: (
                <Anchor
                  href={`https://gbfs.urbansharing.com/${systemDomain}/station_status.json`}
                >
                  station_status.json
                </Anchor>
              ),
              availabilityEndPointDocumentation: (
                <Anchor href="https://github.com/NABSA/gbfs/blob/df473ca4adbff982d67b50ac00b625191591d8f8/gbfs.md#station_statusjson">
                  <FormattedMessage {...messages.documentation} />
                </Anchor>
              )
            }}
          />
        </Text>
        <H4>
          <FormattedMessage {...messages.curl} />
        </H4>
        <CodeBlock>
          {`curl -H "Client-Identifier: IDENTIFIER" \\
  https://gbfs.urbansharing.com/${systemDomain}/station_status.json`}
        </CodeBlock>
        <H4>
          <FormattedMessage {...messages.response} />
        </H4>
        <CodeBlock>
          {JSON.stringify(
            {
              last_updated: 1540219230,
              data: {
                stations: [
                  {
                    is_installed: 1,
                    is_renting: 1,
                    num_bikes_available: 7,
                    num_docks_available: 5,
                    last_reported: 1540219230,
                    is_returning: 1,
                    station_id: '175'
                  },
                  {
                    is_installed: 1,
                    is_renting: 1,
                    num_bikes_available: 4,
                    num_docks_available: 8,
                    last_reported: 1540219230,
                    is_returning: 1,
                    station_id: '47'
                  },
                  {
                    is_installed: 1,
                    is_renting: 1,
                    num_bikes_available: 4,
                    num_docks_available: 9,
                    last_reported: 1540219230,
                    is_returning: 1,
                    station_id: '10'
                  }
                ]
              }
            },
            null,
            2
          )}
        </CodeBlock>

        {showTripHistory && (
          <>
            <H2>
              <FormattedMessage {...messages.historicalDataTitle} />
            </H2>
            <FormattedMessage
              {...messages.historicalDataText}
              values={{
                historicalDataLink: (
                  <Link route="open-data.historical" passHref>
                    <Anchor>
                      <FormattedMessage {...messages.historicalDataLinkText} />
                    </Anchor>
                  </Link>
                )
              }}
            />
          </>
        )}
      </OpenDataWrapper>
    </Container>
  );
};

export default OpenData;
